import Divider from "../components/divider";
import Hero from "../components/hero";
import SeoMeta from "../components/seo-meta";
import { getSEOPageProps } from "../lib/seo";

import styles from "./policy.module.scss";
import config from "../config";

export default function PrivacyPolicyPage({ seo }) {
  return (
    <div className="container">
      <SeoMeta {...seo} />
      <Hero title="East Room Privacy Policy" />

      <div className={styles.Policy}>
        <section className={styles.Intro}>
          <p>Last updated: March 28, 2022</p>

          <p>
            East Room (operating as “East Room Holding Corp”) cares about your
            privacy. We are committed to maintaining your trust by protecting
            your Personal Information. This Privacy Policy (“Policy”) describes
            how East Room collects, uses, discloses, and otherwise processes
            your Personal Information in connection with your use of our website
            (“Website Service”), applications, or other services that refer to
            or link to this Policy, as well as any offline products, programs,
            courses, and services (collectively our “Services”). Before you use
            our Services, please read this Policy in its entirety, to help you
            make informed decisions about your relationship with us.
          </p>
        </section>

        <Divider className={styles.Divider} />

        <section>
          <h2>1. Introduction</h2>

          <p>
            <strong>Personal Information.</strong> “Personal Information” is
            data that relates to you and can — either on its own or in
            combination with other information — identify you as an individual.
            Personal Information does not include data that has been aggregated
            or made anonymous such that you can no longer be identified using
            means reasonably available to us.
          </p>

          <p>
            <strong>Our Services.</strong> The Services referenced in this
            Policy include the Services provided by East Room. You can read more
            about how Personal Information is used by clicking below on the name
            of East Room Services, websites, or applications you use.
          </p>

          <p>
            <strong>References to “you.”</strong> In this Policy, “you” means
            anyone who uses our websites, applications, or other Services.
          </p>
          <p>
            <strong>Authoritative Version.</strong> When the text of this Policy
            is available in multiple languages, the English version is the
            authoritative version, except where prohibited by applicable law.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>

          <p>
            The Personal Information that we collect, and our use of that
            information depends on your relationship with us and the
            requirements of applicable law as described below. We collect
            information:
          </p>

          <ul>
            <li>that you provide to us directly.</li>
            <li>
              that your company, broker, host, parent, or guardian provides to
              us;
            </li>
            <li>
              about your devices and their location, and your use of our
              Services, including through cookies, pixels, web beacons, logs,
              and other Internet technologies. For more information about these
              technologies, please see our Cookie Policy;
            </li>
            <li>
              If you opt-in to certain features or depending on your device
              settings, we obtain geo-location data and information about your
              contacts;{" "}
            </li>
            <li>
              from third parties such as our landlords, building managers,
              licensees and property brokers); and
            </li>
          </ul>

          <p>Information Collected From You:</p>

          <p>
            East Room requests different types of Personal Information from you
            and your company or others, depending on the Services you use and
            your relationship with us. We may collect the following categories
            of information, depending on the Services that you use:
          </p>

          <ul>
            <li>
              <strong>Contact Information and other Identifiers</strong>,
              including your name, mailing address, phone number, email address,
              and other information that enables us to contact you;
            </li>
            <li>
              <strong>Verification Information</strong>, including your personal
              photograph, photo ID or government issued identification, and date
              of birth;
            </li>
            <li>
              <strong>Professional and Employment-Related Information</strong>,
              including your company name, title, role, team, and other
              information about your profession;
            </li>
            <li>
              <strong>Payment Information</strong>, including your credit or
              debit card details, bank account information, and payment or other
              information required when you make a purchase;
            </li>
            <li>
              <strong>Office Preference</strong>s, including your requested or
              preferred temperature, fan speed, desk and chair height, desk
              location, phone booth, beverage, snack, and other preferences
              related to the Services.{" "}
            </li>
            <li>
              <strong>Utilization Information</strong>, including how frequently
              you visit, how long you stay, and what types of Services you use
              when you visit an East Room location. These include services such
              as printer or Wi-fi usage, and desk or conference room bookings;
            </li>
            <li>
              <strong>Communications</strong>, and any other information that
              you intentionally share, send, or receive using the Services,
              including your e-mail, SMS, Slack, requests made through feedback
              platforms, automated chatbots or similar technology, and any other
              information you choose to share with us — including your photos,
              videos, blog entries, interests, hobbies, lifestyle choices, and
              groups with which you are affiliated — and that you send or
              receive using the Services;
            </li>
            <li>
              <strong>Device and Usage Information</strong>, including
              information about your computer or device and Internet or other
              electronic network activity information. This includes:
            </li>
            <li>
              <strong>Device identifiers</strong>, such as IP address, WIFI MAC
              address, and Bluetooth address.{" "}
            </li>
            <li>
              <strong>Information about your online activity</strong>, including
              information collected through the use of standard Internet
              technologies, such as cookies, pixels, web beacons, logs, and
              other Internet technologies, as further set forth in our Cookie
              Policy, and your offline activity, including information about
              events you attend at East Room locations; and
            </li>
          </ul>

          <p>Information Collected About You</p>

          <ul>
            <li>
              <strong>Third-Party Sources</strong>. We obtain Personal
              Information about you from your company, broker, parent or
              guardian, or from your host if you are a visitor or guest to one
              of our spaces.
            </li>
            <li>
              <strong>Google and Other Third-Party Analytics</strong>. We use a
              tool called “Google Analytics” to collect information about the
              use of our Website Services (e.g., Google Analytics collects
              information such as how often users visit the Website Services,
              what pages they visit when they do so, and what other sites they
              used prior to visiting the Website Services). Google Analytics
              collects only the IP address assigned to you on the date that you
              visit the Website Services, rather than your name or other
              identifying information. The information collected through the use
              of Google Analytics is not combined with your Personal
              Information. You can learn more about how Google Analytics
              collects and processes data and opt-out options at{" "}
              <a
                href="http://www.google.com/policies/privacy/partners/"
                target="_blank"
                rel="noreferrer"
              >
                http://www.google.com/policies/privacy/partners/
              </a>
              . We also may use other third-party analytics tools to collect
              similar information about use of certain online Services.
            </li>
            <li>
              <strong>“Do Not Track.”</strong> If you have enabled the feature,
              your web browser may transmit “do-not-track” signals to our
              websites and other online services with which your browser
              communicates. We currently do not take any action in response to
              these signals.
            </li>
            <li>
              <strong>Mobile Computing</strong>. We provide websites and online
              resources that are specifically designed to be compatible and used
              on mobile computing devices. Mobile versions of the Services may
              require you to log in with an account. In such cases, information
              about use of each mobile version of the website may be associated
              with user accounts. In addition, East Room enables you to download
              an application, widget, or other tool that can be used on mobile
              or other computing devices. Some of these tools may store
              information on mobile or other devices. These tools transmit
              Personal Information to East Room to enable you to access your
              user accounts and to enable East Room to track use of these tools.
              Some of these tools enable you to e-mail reports and other
              information from the tool.
            </li>
          </ul>
        </section>

        <section>
          <h2>3. Use of Personal Information</h2>

          <p>
            How we use your Personal Information depends on your relationship
            with us. In general, we use your Personal Information to:
          </p>
          <ul>
            <li>
              provide you with our products and Services, including online and
              offline events, and other materials you request;
            </li>
            <li>
              protect against fraud, abuse, or harm and to otherwise provide a
              Secure Environment (“Secure Environment” as used in this Policy
              means both physical and online environments);
            </li>
            <li>respond to or fulfill customer service requests;</li>
            <li>
              analyze, better tailor, and improve our products and Services;
            </li>
            <li>
              contact you about our Services, promotions, and sweepstakes,
              tailor and provide you with relevant advertising, enhance your
              engagement with us, and otherwise communicate with you through
              various means, which may include by email, telephone, post, or by
              other means as communicated to you from time to time; and
            </li>
            <li>
              comply with our legal obligations and enforce our legal rights,
              such as, among other things, to exercise contractual rights, to
              comply with financial reporting obligations, court orders,
              warrants, or subpoenas in accordance with applicable law.
            </li>
          </ul>
        </section>

        <section>
          <h2>4. Sharing of Personal Information</h2>

          <p>
            We disclose your Personal Information in limited circumstances to
            East Room, our community, our service providers and advertisers,
            other end users, in order to provide our Services and to improve
            ways of working in our community and where required by applicable
            law. Where required by applicable law, we will only share your
            Personal Information with particular third parties with your
            consent. We will require all parties receiving your Personal
            Information to take appropriate measures to protect your Personal
            Information and to process your Personal Information only for the
            purposes described:{" "}
          </p>

          <p>
            <strong>Among East Room</strong>. We may share your Personal
            Information among East Room businesses or entities depending on
            where you are located and the Services that you use. East Room uses
            your information in accordance with this Policy, and we share this
            information for our administrative purposes including activities
            such as IT management, for them to provide services to you, or to
            support and supplement the Services that we provide.
          </p>

          <p>
            <strong>Our Community</strong>. Our Services include online and
            offline member communities, forums, networks, and other features
            through which you may share information and connect with others.
            This includes:
          </p>

          <ul>
            <li>
              Our Services may make member networks, chat rooms, forums, and/or
              other groups available to users. Remember that anyone can read,
              collect, or use your postings on a network or forum or in a chat
              room or group and we cannot be responsible for any unauthorized
              third party’s use of information that you publicly disclose
              online.
            </li>
            <li>
              We foster our community by creating profiles for members or
              permitting members to create their own profiles on member networks
              or forums. Typically, such a profile contains your name, the name
              of your company, and other information you provide or add to your
              profile (content, comments, photos, etc.). Depending on the
              Services you use, this profile might be created automatically and
              be accessible to other members of the network or forum. Member
              Network profile information is accessible to all members. You can
              also share your profile with a broader audience (e.g., via social
              media).
            </li>
            <li>
              When we receive information about you in connection with your
              relationship with our members or clients—for example, if you are a
              member’s employee or if you are visiting one of our clients—we may
              share your information with the member or client with which you
              have such a relationship, for example, by alerting the member or
              client that you have arrived to visit. Please note that we are not
              responsible for the privacy practices of our members or clients,
              or their third-party service providers, including those they
              integrate with our Services.
            </li>
            <li>
              We also may share information about you with others in the
              community to provide you with certain features.
            </li>
          </ul>

          <p>
            <strong>Primary Members</strong>. Information on the utilization of
            our services by individual members may be shared with the primary
            member or the organization who purchased the memberships. Examples
            of individual members are employees of a member company or students
            of a member university.
          </p>

          <p>
            <strong>Landlords and Building Management</strong>. In some cases we
            may share your Personal Information with our landlords and/or
            building management at East Room properties so that they can
            effectively manage and operate the building.
          </p>

          <ul>
            <li>
              <strong>Research / Survey Solicitations</strong>. From time to
              time, we will share your Personal Information to perform research
              (online and offline) via surveys on our behalf. Participation is
              voluntary, and we will use the information that we collect for
              research and reporting purposes, and to help us to improve the
              quality of our Services. We use the survey responses to determine
              the effectiveness of our Services, including the quality of our
              communications and our advertising and/or promotional activities.
              If you participate in a survey, your responses will be used along
              with other participants’ responses.
            </li>
            <li>
              <strong>Third-Party Advertising Partners</strong>. We share
              limited Personal Information with third parties that serve our
              advertising online, so that they can display our advertisements to
              individuals who may find them most relevant. We share this
              information in order to meet our legitimate interests in
              advertising our products effectively.
            </li>
            <li>
              <strong>
                Other Parties When Required by Law and as Necessary to Provide
                and Protect Our Services
              </strong>
              . There may be instances when we disclose your Personal
              Information to other parties:
              <ul>
                <li>
                  to provide you with the Services that you or your company
                  request;
                </li>
                <li>
                  to comply with our legal obligations, including with auditors,
                  lawyers, CPAs, financial consultants, or other consultants;
                </li>
                <li>
                  to comply with applicable law or respond to a legal process or
                  a request for cooperation by a government entity or law
                  enforcement;
                </li>
                <li>
                  to detect, limit, and prevent fraud, or verify and enforce
                  compliance with the policies governing our Services; or
                </li>
                <li>
                  to protect our rights, property, and safety or that of any of
                  the East Room companies, business partners, members, or
                  employees; and
                </li>
                <li>where otherwise required by applicable law.</li>
              </ul>
            </li>
          </ul>

          <p>
            We share Personal Information in this way either to comply with
            legal obligations to which we are subject, or to meet our legitimate
            interests in protecting our business, including from fraud and legal
            claims.
          </p>

          <p>
            <strong>
              Other Parties in Connection with a Corporate Transaction
            </strong>
            . We disclose or share your information in the event that we
            purchase, sell, finance, transfer, or acquire all or a portion of a
            business or assets, such as in connection with a merger, or in the
            event of a bankruptcy reorganization or liquidation.
          </p>

          <p>
            <strong>Other Third-Party Partners, With Your Consent</strong>. We
            may request your consent to share Personal Information about you
            with certain third parties so that they can provide you with special
            offers, promotional materials, and other materials that may be of
            interest to you. With your consent, we also use and publicize your
            name, likeness, and other personal characteristics and private
            information for advertising, promotion, and other commercial and
            business purposes.
          </p>
        </section>

        <section>
          <h2>
            4.1 Third Parties That Provide Content or Functionality on Our
            Services
          </h2>

          <p>
            How we use your Personal Information depends on your relationship
            with us. In general, we use your Personal Information to:
          </p>
          <ul>
            <li>
              provide you with our products and Services, including online and
              offline events, and other materials you request;
            </li>
            <li>
              protect against fraud, abuse, or harm and to otherwise provide a
              Secure Environment (“Secure Environment” as used in this Policy
              means both physical and online environments);
            </li>
            <li>respond to or fulfill customer service requests;</li>
            <li>
              analyze, better tailor, and improve our products and Services;
            </li>
            <li>
              contact you about our Services, promotions, and sweepstakes,
              tailor and provide you with relevant advertising, enhance your
              engagement with us, and otherwise communicate with you through
              various means, which may include by email, telephone, post, or by
              other means as communicated to you from time to time; and
            </li>
            <li>
              comply with our legal obligations and enforce our legal rights,
              such as, among other things, to exercise contractual rights, to
              comply with financial reporting obligations, court orders,
              warrants, or subpoenas in accordance with applicable law.
            </li>
          </ul>

          <p>
            We partner with certain third parties to collect, analyze, and use
            some of the Personal Information described in this Policy. For
            example:
          </p>

          <ul>
            <li>
              Third parties that provide features and functionality on the
              Services by means of plug-ins. Even if you do not click on or
              interact with social networking services or other plug-ins, they
              collect information about you, such as your IP address and the
              pages that you view.
            </li>
            <li>
              Advertising providers help us, and our advertisers provide
              advertisements on our Services or elsewhere, including
              advertisements that are targeted based on your online behavior,
              and analytics companies help us measure and evaluate the usage of
              our services.
            </li>
            <li>
              Other content providers offer products and services on our
              Services and operate contests, sweepstakes, or surveys on our
              Services.
            </li>
          </ul>

          <p>
            We, as well as certain of these third-party partners may use
            tracking tools (e.g., cookies, web beacons, and Internet
            technologies) to collect information regarding your activities
            (e.g., your IP address or page(s) visited). For more information on
            these types of tracking tools, please see our Cookie Policy. We also
            share such information that we have collected with third-party
            advertising partners. These advertising partners use this
            information (and similar information collected from other websites)
            to deliver future targeted advertisements to you when you visit
            non-East Room related websites within their networks. This practice
            is commonly referred to as “interest-based advertising” or “online
            behavioral advertising.”
          </p>

          <p>
            Some of these companies participate in industry-developed programs
            designed to provide consumers choices about whether to receive
            targeted advertising. Please visit the website operated by the
            Digital Advertising Alliance of Canada.
          </p>
        </section>

        <section>
          <h2>4.2 Social Media</h2>

          <p>
            We are active on social media (e.g., Instagram, Facebook, etc.) and
            share information, images, and videos with the public through
            unaffiliated external social media sites. We encourage you to read
            their policies. Remember that the social media sites are public,
            which means that anyone can see your posts, and your posts may even
            show up in search engine results.
          </p>

          <p>
            While we will not edit comments, we may delete a comment from our
            page on a social media site if it, for example, does not relate to
            the post, promotes a commercial product or service, uses obscene,
            threatening, or harassing language, or if it is a personal attack or
            hate speech that targets or disparages any ethnic, racial, age, or
            religious group (or other legally protected group), or if it
            advocates illegal activity or violates copyright laws.
          </p>
        </section>

        <section>
          <h2>
            5. Rights in Your Information: Access, Rectification, Erasure, and
            Restriction
          </h2>

          <p>
            We value your rights to your data. In accordance with applicable
            law, you may have the right to:
          </p>

          <ol>
            <li>
              request confirmation of whether we store, use, or share any of
              your Personal Information and be informed about third parties with
              whom your Personal Information has been shared;
            </li>
            <li>obtain access to or a copy of your Personal Information; </li>
            <li>
              receive an electronic copy of the Personal Information that you
              have provided to us, or ask us to send that information to another
              company (the “right of data portability”);{" "}
            </li>
            <li>
              restrict our uses of your Personal Information or, as described
              above, object to those uses or restrict our sharing of your
              Personal Information;{" "}
            </li>
            <li>
              seek correction of inaccurate, partial, untrue, or incomplete
              Personal Information; In some cases, we may provide self-service
              tools that enable you to update your Personal Information;{" "}
            </li>
            <li>
              request erasure, anonymization, or blocking of Personal
              Information held about you by East Room, subject to certain
              exceptions prescribed by law, when processing is based on your
              consent or when processing is unnecessary, excessive or
              noncompliant;{" "}
            </li>
            <li>
              withdraw your consent to our processing of your Personal
              Information. If you refrain from providing Personal Information or
              withdraw your consent to processing, some features of our Service
              may not be available;{" "}
            </li>
            <li>
              object to an automated decision East Room has made and request the
              review of decisions taken exclusively based on automated
              processing if that could affect data subject rights; or{" "}
            </li>
            <li>cancel your online account if one has been created. </li>
          </ol>

          <p>
            If you would like to exercise any of these rights, please contact us
            at info@eastroom.com or at one of the physical addresses.
          </p>

          <p>
            We will process all such requests in accordance with local laws. To
            protect your privacy, East Room will take steps to verify your
            identity before granting access or making any changes to your
            Personal Information.
          </p>
        </section>

        <section>
          <h2>6. Opt-Our and Withdrawal of Consent</h2>
          <p>
            You may have the right to opt-out or withdraw your consent for
            certain uses and disclosures of your Personal Information.
          </p>
        </section>

        <section>
          <h2>6.1 Sensitive Personal Information</h2>

          <p>
            Where you have consented to East Room’s use of “sensitive” Personal
            Information (e.g., Personal Information relating to your health,
            religious or philosophical beliefs, or biometric information which
            is used to uniquely identify you) (“Sensitive Personal
            Information”), you may withdraw that consent, in accordance with
            applicable law.
          </p>

          <p>
            Prior to disclosing Sensitive Personal Information to a third party
            or processing Sensitive Personal Information for a purpose other
            than its original purpose or a purpose authorized subsequently by
            the data subject, East Room will endeavour to obtain your explicit
            consent (opt-in). Where your consent for the processing of Personal
            Information is otherwise required by law or contract, East Room will
            comply with the law or contract.
          </p>
        </section>

        <section>
          <h2>6.2 Email and Telephone Communications</h2>
          <ul>
            <li>
              <strong>Opting Out of Marketing Communications</strong>. We will
              provide a means to unsubscribe in our marketing-related email
              communications. Note that we will continue to send
              transaction-related emails regarding products or services that you
              have requested following an unsubscribe. We may also need to send
              you certain communications regarding operation and administration
              of the Services, and you will not be able to opt-out of those
              communications, e.g., communications regarding updates to our
              Terms & Conditions or this Policy. If you no longer desire to be
              contacted by us, you must terminate your relationship with East
              Room and can no longer use our Services.
            </li>
            <li>
              <strong>Collaboration</strong>. We do not sell your Personal
              Information to third parties for their direct marketing purposes.
              We may partner with other organizations to run events or programs.
              The Personal Information and other information that you provide to
              sign up or participate in that event or program will be available
              to that organization, and you may receive emails or other
              communications from it after the event. Please refer to that
              organization’s privacy policy for information on how to opt-out of
              its communications.
            </li>
            <li>
              <strong>Text Messages</strong>. We may send you SMS text messages
              related to our Services or transactions. You can opt-out of these
              messages at any time by replying STOP.{" "}
            </li>
          </ul>
        </section>

        <section>
          <h2>7. Complaints and Suprevisory Authority</h2>

          <p>
            If you would like to make a complaint or if you have any questions
            about how we use or keep your Personal Information, you may contact
            East Room at info@eastroom.com
          </p>

          <p>
            For visitors to our Services you may have the right to lodge a
            complaint with the relevant supervisory authority for your
            jurisdiction, if you believe our processing of your Personal
            Information violates applicable law.
          </p>
        </section>

        <section>
          <h2>8. International Transfers</h2>
          <p>
            East Room is headquartered in Canda. Your information will likely be
            collected in your country and subsequently transferred to or stored
            in another country, including countries that may have privacy
            protections less stringent than your jurisdiction. In accordance
            with applicable law, we implement measures, such as standard
            contractual clauses, to ensure that any transferred Personal
            Information remains protected and secure.
          </p>
        </section>

        <section>
          <h2>9. Retention</h2>

          <p>
            {" "}
            We retain the personal information we receive for as long as you use
            our Services or as necessary to fulfill the purpose(s) for which it
            was collected, provide our Services, resolve disputes, establish
            legal defences, conduct audits, pursue legitimate business purposes,
            enforce our agreements, and comply with applicable laws.
          </p>

          <p>
            When determining how long this retention period will last once the
            purpose of processing the Personal Information has been satisfied,
            we take into account the length of time Personal Information is
            required to:
          </p>

          <ul>
            <li>
              Continue to develop, tailor, upgrade, and improve our Services;
            </li>
            <li>
              Maintain business records for analysis and/or audit purposes;
            </li>
            <li>Comply with record retention requirements under the law;</li>
            <li>Defend or bring any existing or potential legal claims; or</li>
            <li>Address any complaints regarding the Services.</li>
          </ul>
        </section>

        <section>
          <h2>10. Information Security</h2>

          <p>
            {" "}
            The security of all Personal Information provided to East Room is
            important to us. East Room takes reasonable steps endeavouring to
            use appropriate technical or organizational measures to protect your
            Personal Information, including against unauthorized or unlawful
            processing and accidental loss, destruction, or damage.{" "}
          </p>

          <p>
            By using our Services or providing Personal Information to us, you
            agree that we may communicate with you electronically regarding
            security, privacy, and administrative issues relating to your use of
            our Services. If we learn of a security system’s breach, we may
            attempt to notify you electronically by posting a notice on our
            Services, by mail or by sending an email to you.
          </p>
        </section>

        <section>
          <h2>11. Other Rights and Important Features</h2>
          <h2>11.1 CCTV</h2>
          <p>
            {" "}
            East Room’s buildings and facilities have CCTV installed to meet our
            legitimate business interests in protecting the safety of East
            Room’s employees, members, and guests and their property. These
            recordings may be accessed by East Room. East Room may also make
            CCTV footage available to members and other third parties authorized
            by East Room. These recordings may be shared with competent
            authorities in accordance with applicable laws, if required. The
            recordings are generally retained for a limited period of time but
            may be kept longer where East Room or an applicable member
            determines that it is appropriate, for example in connection with
            the investigation of an incident, or when competent authorities
            request us to retain them.
          </p>

          <p>
            East Room’s CCTV practices are based on an assessment of the data
            protection interests of affected data subjects against our
            legitimate interests. This may entail consideration, for example, of
            the likelihood and impact of unlawful or impermissible conduct on
            our communities and expectations of privacy in particular facilities
            or spaces that may be subject to CCTV. The recordings are generally
            retained for a limited period of time in accordance with Section 9
            of this Privacy Policy.
          </p>
        </section>

        <section>
          <h2>11.2 Collecting of Information from Children</h2>
          <p>
            Our Services are not directed to children, and we do not knowingly
            collect Personal Information from children except as permitted under
            applicable law. If we become aware that a child has provided us with
            Personal Information, we will delete such information from our files
            or obtain parental consent in accordance with applicable law.
          </p>
        </section>

        <section>
          <h2>11.3 Third Parties</h2>
          <p>
            This Privacy Policy does not address the privacy, data, services or
            other practices of any entities other than East Room, including
            landlords, building managers, licensees and brokers.{" "}
          </p>

          <p>
            We display advertisements and other content from third parties or
            partners that link to third-party websites that we do not own or
            operate. We provide links to these third-party sites as a
            convenience to you. They are not intended as an endorsement of or
            referral to the linked services. The linked services are subject to
            their separate and independent privacy statements, notices, and
            terms. Additionally, third parties may sell goods or offer services
            at our facilities or in combination with our Services. We are not
            responsible for those third parties’ services or their privacy
            practices.
          </p>

          <p>
            We cannot control or be held responsible for third parties’ privacy
            practices and content. Please read their privacy policies to find
            out how they collect and process your Personal Information prior to
            submitting any Personal Information. The collection, use, and
            disclosure of your Personal Information will be subject to the
            privacy policies of the third party and not this Policy. We are not
            responsible for the data collection, privacy, and information
            sharing policies and procedures or content of such third-parties.
          </p>
        </section>

        <section>
          <h2>11.4 Changes to This Policy in the Future</h2>

          <p>
            Any information that is collected via the Services is covered by the
            Policy in effect at the time such information is collected. We
            reserve the right to change, modify, add, or remove portions of this
            Policy at any time and at our sole discretion.
          </p>

          <p>
            If we make any material changes to this Policy, we will notify you
            of those changes by sending you an email or other notification, as
            required by applicable law, and we will update the “Last Updated”
            date to indicate when those changes became effective.
          </p>

          <p>
            We encourage you to review this Policy periodically to remain
            informed of how we use and protect your information, and to be aware
            of any policy changes. Your continued relationship with us after the
            posting or notice of any amended Policy shall constitute your
            agreement to be bound by any such changes. Any changes to this
            Policy take effect immediately after being posted or otherwise
            provided by East Room.
          </p>
        </section>

        <section>
          <h2>11.5 Questions or Comments</h2>
          <p>
            If you have any questions or comments regarding this Policy, please
            contact us at info@eastroom.com. East Room will address your
            concerns and attempt to resolve any privacy issues in a timely
            manner.
          </p>

          <p>
            <strong>Sensitive Information</strong>. Because email communications
            are not always secure, please do not include credit card or other
            Sensitive Personal Information (e.g., racial or ethnic origin,
            religion, health, or the like) in your emails to us.
          </p>
        </section>
      </div>
    </div>
  );
}

export async function getStaticProps() {
  return {
    props: { seo: await getSEOPageProps({ resolvedUrl: "/privacy-policy" }) },
    revalidate: config.ISR_TIMING
  };
}
